import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowUp, FaArrowDown, FaTrash, FaPlus, FaSave, FaEdit, FaImage, FaTimes } from 'react-icons/fa';
import api from '../../services/api';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Register fonts if needed, similar to Blogs.js
var Font = Quill.import('formats/font');
Font.whitelist = ['arial', 'georgia', 'times-new-roman', 'verdana'];
Quill.register(Font, true);

const TECH_ICONS = [
  { label: 'None', value: '' },
  { label: 'React', value: 'logos:react' },
  { label: 'Node.js', value: 'logos:nodejs-icon' },
  { label: 'JavaScript', value: 'logos:javascript' },
  { label: 'TypeScript', value: 'logos:typescript-icon' },
  { label: 'Python', value: 'logos:python' },
  { label: 'AWS', value: 'logos:aws' },
  { label: 'Docker', value: 'logos:docker-icon' },
  { label: 'Kubernetes', value: 'logos:kubernetes' },
  { label: 'MongoDB', value: 'logos:mongodb-icon' },
  { label: 'PostgreSQL', value: 'logos:postgresql' },
  { label: 'MySQL', value: 'logos:mysql-icon' },
  { label: 'Redis', value: 'logos:redis' },
  { label: 'Next.js', value: 'logos:nextjs-icon' },
  { label: 'Vue.js', value: 'logos:vue' },
  { label: 'Angular', value: 'logos:angular-icon' },
  { label: 'Tailwind CSS', value: 'logos:tailwindcss-icon' },
  { label: 'Bootstrap', value: 'logos:bootstrap' },
  { label: 'Firebase', value: 'logos:firebase' },
  { label: 'Google Cloud', value: 'logos:google-cloud' },
  { label: 'Azure', value: 'logos:microsoft-azure' },
  { label: 'Figma', value: 'logos:figma' },
  { label: 'Adobe XD', value: 'logos:adobe-xd' },
  { label: 'GitHub', value: 'logos:github-icon' },
  { label: 'GitLab', value: 'logos:gitlab' },
  { label: 'Bitbucket', value: 'logos:bitbucket' },
  { label: 'Jira', value: 'logos:jira' },
  { label: 'Trello', value: 'logos:trello' },
  { label: 'Slack', value: 'logos:slack-icon' },
];

const SECTION_TEMPLATES = {
  sub_header: { title: '', sub_points: [], mockupImage: '', ctaText: 'Request a Quote' },
  about: { brand: '', location: '', industry: '', type: '', workScope: '', productLink: '', image: '' },
  brochure: { title: 'Project Brochure', description: '', items: [] }, // items: [{ type: 'image', url: '', caption: '' }]
  features: { title: '', items: [] }, // items: [{ title: '', sub_features: [] }]
  challenge: { challenges: [], image: '' },
  solution: { desc: '', points: [], image: '' },
  results: { desc: '', metrics: [] }, // metrics: [{ title: '', value: '', extra: '' }]
  technologies: { title: 'Technologies', items: [] }, // items: [{ name: '', icon: '' }]
  competitiveComparison: { headers: ['Us', 'Others'], rows: [] }, // rows: [{ feature: '', us: true, them: false }]
  clients: { items: [] } // items: [{ name: '', title: '', review: '', stars: 5, image: '' }]
};

const ProjectEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [sections, setSections] = useState([]);
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);

  // Project Details State
  const [projectDetails, setProjectDetails] = useState({
    title: '',
    slug: '',
    category: '',
    shortDescription: '',
    liveLink: '',
    isFeatured: false
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const data = await api.get(`projects`);
        const p = data.find(p => p.id === parseInt(id));
        if (p) {
          setProject(p);
          setSections(p.sections || []);
          setProjectDetails({
            title: p.title,
            slug: p.slug,
            category: p.category,
            shortDescription: p.shortDescription || '',
            liveLink: p.liveLink || '',
            isFeatured: p.isFeatured
          });
          if (p.thumbnailUrl) {
            setThumbnailPreview(`${process.env.REACT_APP_API_BASE_URL}/${p.thumbnailUrl}`);
          }
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      }
    };
    fetchProject();
  }, [id]);

  const handleDetailsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProjectDetails(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnail(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const saveProjectDetails = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    Object.keys(projectDetails).forEach(key => formData.append(key, projectDetails[key]));
    if (thumbnail) formData.append('image', thumbnail);

    try {
      const updated = await api.put(`projects/${id}`, formData);
      setProject(updated);
      setIsEditingDetails(false);
      alert('Project details updated!');
    } catch (error) {
      console.error('Error updating project details:', error);
      alert('Failed to update details: ' + error.message);
    }
  };

  const handleAddSection = async (type) => {
    try {
      const newSection = {
        type,
        order: sections.length,
        content: SECTION_TEMPLATES[type]
      };
      const savedSection = await api.post(`projects/${id}/sections`, newSection);
      setSections([...sections, savedSection]);
      setIsAddingSection(false);
    } catch (error) {
      console.error('Error adding section:', error);
      alert('Failed to add section: ' + error.message);
    }
  };

  const handleUpdateSection = async (sectionId, updatedContent) => {
    try {
      const section = sections.find(s => s.id === sectionId);
      const updatedSection = await api.put(`projects/sections/${sectionId}`, {
        ...section,
        content: updatedContent
      });
      setSections(sections.map(s => s.id === sectionId ? updatedSection : s));
      alert('Section updated successfully!');
    } catch (error) {
      console.error('Error updating section:', error);
      alert('Failed to update section: ' + error.message);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section?')) return;
    try {
      await api.delete(`projects/sections/${sectionId}`);
      setSections(sections.filter(s => s.id !== sectionId));
    } catch (error) {
      console.error('Error deleting section:', error);
      alert('Failed to delete section: ' + error.message);
    }
  };

  const moveSection = async (index, direction) => {
    const newSections = [...sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;

    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    
    setSections(newSections);

    try {
        await Promise.all(newSections.map((s, i) => 
            api.put(`projects/sections/${s.id}`, { ...s, order: i })
        ));
        setSections(newSections.map((s, i) => ({ ...s, order: i })));
    } catch (error) {
        console.error('Error reordering sections:', error);
    }
  };

  if (!project) return <div className="p-8 text-center text-gray-500">Loading project details...</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans">
      <div className="max-w-5xl mx-auto">
        {/* Header & Back Button */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/projects')} className="text-gray-600 hover:text-gray-900 transition flex items-center gap-1">
              &larr; Back to Projects
            </button>
            <h1 className="text-3xl font-bold text-gray-800">{project.title}</h1>
          </div>
          <button 
            onClick={() => setIsEditingDetails(true)} 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2 shadow-sm"
          >
            <FaEdit /> Edit Details
          </button>
        </div>

        {/* Project Details Modal */}
        {isEditingDetails && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
                <h2 className="text-xl font-bold">Edit Project Details</h2>
                <button onClick={() => setIsEditingDetails(false)} className="text-gray-500 hover:text-gray-700"><FaTimes size={24}/></button>
              </div>
              <form onSubmit={saveProjectDetails} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <input type="text" name="title" value={projectDetails.title} onChange={handleDetailsChange} className="mt-1 w-full p-2 border rounded-lg" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Slug</label>
                    <input type="text" name="slug" value={projectDetails.slug} onChange={handleDetailsChange} className="mt-1 w-full p-2 border rounded-lg" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Category</label>
                    <select name="category" value={projectDetails.category} onChange={handleDetailsChange} className="mt-1 w-full p-2 border rounded-lg">
                      <option value="Web App Development">Web App Development</option>
                      <option value="Mobile App Development">Mobile App Development</option>
                      <option value="AI-Driven">AI-Driven</option>
                      <option value="B2B">B2B</option>
                      <option value="B2C">B2C</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Live Link</label>
                    <input type="text" name="liveLink" value={projectDetails.liveLink} onChange={handleDetailsChange} className="mt-1 w-full p-2 border rounded-lg" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Short Description</label>
                  <textarea name="shortDescription" value={projectDetails.shortDescription} onChange={handleDetailsChange} rows={3} className="mt-1 w-full p-2 border rounded-lg" />
                </div>

                <div className="flex items-center gap-2">
                  <input type="checkbox" name="isFeatured" checked={projectDetails.isFeatured} onChange={handleDetailsChange} id="isFeatured" className="w-4 h-4 text-blue-600" />
                  <label htmlFor="isFeatured" className="text-sm font-medium text-gray-700">Feature this project on home page</label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Thumbnail Image</label>
                  <div className="flex items-center gap-4">
                    {thumbnailPreview && <img src={thumbnailPreview} alt="Preview" className="w-24 h-24 object-cover rounded-lg border" />}
                    <input type="file" onChange={handleThumbnailChange} accept="image/*" className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                  </div>
                </div>

                <div className="pt-4 border-t flex justify-end gap-3">
                  <button type="button" onClick={() => setIsEditingDetails(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Sections List */}
        <div className="space-y-8">
          {sections.sort((a,b) => a.order - b.order).map((section, index) => (
            <div key={section.id} className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden transition hover:shadow-lg">
              <div className="bg-gray-50 p-4 border-b flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded uppercase tracking-wide">{section.type.replace(/_/g, ' ')}</span>
                  <span className="text-gray-400 text-sm">Section #{index + 1}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => moveSection(index, 'up')} disabled={index === 0} className="p-2 hover:bg-white hover:text-blue-600 rounded-full disabled:opacity-30 disabled:hover:bg-transparent" title="Move Up"><FaArrowUp /></button>
                  <button onClick={() => moveSection(index, 'down')} disabled={index === sections.length - 1} className="p-2 hover:bg-white hover:text-blue-600 rounded-full disabled:opacity-30 disabled:hover:bg-transparent" title="Move Down"><FaArrowDown /></button>
                  <button onClick={() => handleDeleteSection(section.id)} className="p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 rounded-full transition" title="Delete"><FaTrash /></button>
                </div>
              </div>
              
              <div className="p-6">
                <SectionForm 
                    type={section.type} 
                    content={section.content} 
                    onSave={(updated) => handleUpdateSection(section.id, updated)} 
                />
              </div>
            </div>
          ))}
        </div>

        {/* Add Section Button */}
        <div className="mt-10 mb-20 flex flex-col items-center">
          {!isAddingSection ? (
            <button 
              onClick={() => setIsAddingSection(true)}
              className="group bg-white border-2 border-dashed border-gray-300 text-gray-500 px-8 py-4 rounded-xl font-bold flex items-center gap-2 hover:border-blue-500 hover:text-blue-600 transition-all w-full max-w-lg justify-center shadow-sm"
            >
              <FaPlus className="group-hover:scale-110 transition-transform" /> Add New Section
            </button>
          ) : (
            <div className="bg-white p-6 rounded-xl shadow-xl border w-full max-w-2xl">
              <h3 className="font-bold text-lg mb-6 text-center text-gray-800">Select Section Type</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.keys(SECTION_TEMPLATES).map(type => (
                  <button 
                    key={type} 
                    onClick={() => handleAddSection(type)}
                    className="p-3 border rounded-lg hover:bg-blue-50 hover:border-blue-500 capitalize text-sm font-medium text-gray-700 transition-colors"
                  >
                    {type.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
              <button onClick={() => setIsAddingSection(false)} className="mt-6 w-full py-2 text-gray-500 hover:text-gray-800 text-sm font-medium">Cancel</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Sub-components for Section Forms ---

const ImageUpload = ({ label, value, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await api.post('projects/sections/image', formData);
      const fullUrl = `${process.env.REACT_APP_API_BASE_URL}/${res.imageUrl}`;
      onChange(fullUrl);
    } catch (error) {
      console.error("Upload failed", error);
      alert("Image upload failed: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label || "Image"}</label>
      <div className="flex items-start gap-4">
        {value && (
          <div className="relative w-32 h-32 bg-gray-100 rounded-lg overflow-hidden border">
            <img src={value} alt="Preview" className="w-full h-full object-cover" />
            <button onClick={() => onChange('')} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 text-xs"><FaTrash/></button>
          </div>
        )}
        <div className="flex-1">
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
            <FaImage /> {uploading ? 'Uploading...' : 'Upload Image'}
            <input type="file" className="hidden" accept="image/*" onChange={handleUpload} disabled={uploading} />
          </label>
          <p className="text-xs text-gray-500 mt-1">Or paste URL below:</p>
          <input 
            type="text" 
            value={value} 
            onChange={(e) => onChange(e.target.value)} 
            placeholder="https://..." 
            className="mt-1 w-full p-2 text-sm border rounded bg-gray-50" 
          />
        </div>
      </div>
    </div>
  );
};

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['link', 'clean']
    ],
  };

  return (
    <div className="prose-editor">
      <ReactQuill 
        theme="snow" 
        value={value || ''} 
        onChange={onChange} 
        modules={modules}
        placeholder={placeholder}
        className="bg-white rounded-lg"
      />
    </div>
  );
};

const SectionForm = ({ type, content, onSave }) => {
  const [data, setData] = useState(content);

  // Sync internal state if props change (e.g. reorder)
  useEffect(() => { setData(content); }, [content]);

  const handleChange = (field, value) => setData(prev => ({ ...prev, [field]: value }));
  
  // List handlers
  const handleListChange = (field, index, value) => {
    const newList = [...data[field]];
    newList[index] = value;
    setData({ ...data, [field]: newList });
  };
  const addListItem = (field, val = '') => setData({ ...data, [field]: [...data[field], val] });
  const removeListItem = (field, idx) => setData({ ...data, [field]: data[field].filter((_, i) => i !== idx) });

  // Object List handlers
  const handleObjChange = (list, idx, key, val) => {
    const newList = [...data[list]];
    newList[idx] = { ...newList[idx], [key]: val };
    setData({ ...data, [list]: newList });
  };
  const addObjItem = (list, obj) => setData({ ...data, [list]: [...data[list], obj] });

  // Nested List handlers
  const handleNestedListChange = (list, idx, nestedKey, nestedIdx, val) => {
    const newList = [...data[list]];
    const newNested = [...newList[idx][nestedKey]];
    newNested[nestedIdx] = val;
    newList[idx] = { ...newList[idx], [nestedKey]: newNested };
    setData({ ...data, [list]: newList });
  };
  const addNestedItem = (list, idx, nestedKey, val = '') => {
    const newList = [...data[list]];
    const newNested = [...(newList[idx][nestedKey] || []), val];
    newList[idx] = { ...newList[idx], [nestedKey]: newNested };
    setData({ ...data, [list]: newList });
  };
  const removeNestedItem = (list, idx, nestedKey, nestedIdx) => {
    const newList = [...data[list]];
    newList[idx][nestedKey] = newList[idx][nestedKey].filter((_, i) => i !== nestedIdx);
    setData({ ...data, [list]: newList });
  };

  const renderFields = () => {
    switch(type) {
      case 'sub_header':
        return (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <input type="text" placeholder="Section Title" value={data.title} onChange={e => handleChange('title', e.target.value)} className="w-full p-2 border rounded" />
              <input type="text" placeholder="CTA Text" value={data.ctaText} onChange={e => handleChange('ctaText', e.target.value)} className="w-full p-2 border rounded" />
              <div>
                <label className="block text-sm font-medium mb-2">Key Points</label>
                {data.sub_points.map((p, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input type="text" value={p} onChange={e => handleListChange('sub_points', i, e.target.value)} className="flex-1 p-2 border rounded" />
                    <button onClick={() => removeListItem('sub_points', i)} className="text-red-500"><FaTrash/></button>
                  </div>
                ))}
                <button onClick={() => addListItem('sub_points')} className="text-blue-600 text-sm font-bold">+ Add Point</button>
              </div>
            </div>
            <div>
              <ImageUpload label="Mockup Image" value={data.mockupImage} onChange={val => handleChange('mockupImage', val)} />
            </div>
          </div>
        );

      case 'about':
        return (
          <div className="grid md:grid-cols-2 gap-4">
            <input type="text" placeholder="Brand Name" value={data.brand} onChange={e => handleChange('brand', e.target.value)} className="p-2 border rounded" />
            <input type="text" placeholder="Location" value={data.location} onChange={e => handleChange('location', e.target.value)} className="p-2 border rounded" />
            <input type="text" placeholder="Industry" value={data.industry} onChange={e => handleChange('industry', e.target.value)} className="p-2 border rounded" />
            <input type="text" placeholder="Work Scope" value={data.workScope} onChange={e => handleChange('workScope', e.target.value)} className="p-2 border rounded" />
            <input type="text" placeholder="Product Link" value={data.productLink} onChange={e => handleChange('productLink', e.target.value)} className="p-2 border rounded col-span-2" />
            <div className="col-span-2">
              <ImageUpload label="About Section Image" value={data.image} onChange={val => handleChange('image', val)} />
            </div>
          </div>
        );

      case 'brochure':
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <input type="text" placeholder="Section Title" value={data.title} onChange={e => handleChange('title', e.target.value)} className="p-2 border rounded font-bold" />
              <input type="text" placeholder="Section Description" value={data.description} onChange={e => handleChange('description', e.target.value)} className="p-2 border rounded" />
            </div>
            <div className="space-y-4">
              <label className="block text-sm font-bold text-gray-700">Brochure Items (Images & Videos)</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.items?.map((item, i) => (
                  <div key={i} className="border p-4 rounded-lg bg-white shadow-sm relative space-y-3">
                    <button onClick={() => removeListItem('items', i)} className="absolute top-2 right-2 text-red-400 hover:text-red-600"><FaTrash/></button>
                    
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Type</label>
                        <select 
                          value={item.type} 
                          onChange={e => handleObjChange('items', i, 'type', e.target.value)}
                          className="w-full p-2 border rounded text-sm bg-gray-50"
                        >
                          <option value="image">Image</option>
                          <option value="video">Video URL (YouTube/Vimeo)</option>
                        </select>
                      </div>
                      <div className="flex-[2]">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Caption</label>
                        <input 
                          type="text" 
                          placeholder="Short caption..." 
                          value={item.caption} 
                          onChange={e => handleObjChange('items', i, 'caption', e.target.value)} 
                          className="w-full p-2 border rounded text-sm"
                        />
                      </div>
                    </div>

                    {item.type === 'image' ? (
                      <ImageUpload 
                        label="Media File" 
                        value={item.url} 
                        onChange={val => handleObjChange('items', i, 'url', val)} 
                      />
                    ) : (
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Video Link</label>
                        <input 
                          type="text" 
                          placeholder="https://youtube.com/watch?v=..." 
                          value={item.url} 
                          onChange={e => handleObjChange('items', i, 'url', e.target.value)} 
                          className="w-full p-2 border rounded text-sm bg-gray-50"
                        />
                      </div>
                    )}
                  </div>
                ))}
                <button 
                  onClick={() => addObjItem('items', { type: 'image', url: '', caption: '' })} 
                  className="border-2 border-dashed border-cyan-200 rounded-lg flex flex-col items-center justify-center p-8 text-cyan-600 hover:bg-cyan-50 transition-colors group"
                >
                  <FaPlus className="text-2xl mb-2 group-hover:scale-110 transition-transform" />
                  <span className="font-bold">Add Gallery Item</span>
                </button>
              </div>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-6">
            <input type="text" placeholder="Section Title" value={data.title} onChange={e => handleChange('title', e.target.value)} className="w-full p-2 border rounded font-bold" />
            <div className="grid md:grid-cols-2 gap-4">
              {data.items?.map((item, i) => (
                <div key={i} className="border p-4 rounded-lg bg-gray-50 relative">
                  <button onClick={() => removeListItem('items', i)} className="absolute top-2 right-2 text-red-400 hover:text-red-600"><FaTimes/></button>
                  <input type="text" placeholder="Feature Title" value={item.title} onChange={e => handleObjChange('items', i, 'title', e.target.value)} className="w-full p-2 border rounded mb-3 font-medium" />
                  
                  <div className="space-y-2">
                    {item.sub_features?.map((sub, j) => (
                      <div key={j} className="flex gap-2">
                        <input type="text" value={sub} onChange={e => handleNestedListChange('items', i, 'sub_features', j, e.target.value)} className="flex-1 p-1 border rounded text-sm" placeholder="Sub-feature" />
                        <button onClick={() => removeNestedItem('items', i, 'sub_features', j)} className="text-red-300 hover:text-red-500"><FaTimes size={12}/></button>
                      </div>
                    ))}
                    <button onClick={() => addNestedItem('items', i, 'sub_features')} className="text-blue-500 text-xs font-bold">+ Add Sub-feature</button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => addObjItem('items', { title: '', sub_features: [] })} className="w-full py-2 border-2 border-dashed border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50">+ Add Feature Column</button>
          </div>
        );

      case 'challenge':
        return (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Challenge Points</label>
              {data.challenges?.map((c, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <textarea value={c} onChange={e => handleListChange('challenges', i, e.target.value)} className="flex-1 p-2 border rounded" rows={2} />
                  <button onClick={() => removeListItem('challenges', i)} className="text-red-500 self-start mt-2"><FaTrash/></button>
                </div>
              ))}
              <button onClick={() => addListItem('challenges')} className="text-blue-600 text-sm font-bold">+ Add Challenge</button>
            </div>
            <ImageUpload label="Challenge Section Image" value={data.image} onChange={val => handleChange('image', val)} />
          </div>
        );

      case 'solution':
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Overview</label>
                  <RichTextEditor value={data.desc} onChange={val => handleChange('desc', val)} placeholder="Describe the solution..." />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Key Points</label>
                  {data.points?.map((p, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input type="text" value={p} onChange={e => handleListChange('points', i, e.target.value)} className="flex-1 p-2 border rounded" />
                      <button onClick={() => removeListItem('points', i)} className="text-red-500"><FaTrash/></button>
                    </div>
                  ))}
                  <button onClick={() => addListItem('points')} className="text-blue-600 text-sm font-bold">+ Add Point</button>
                </div>
              </div>
              <ImageUpload label="Solution Image" value={data.image} onChange={val => handleChange('image', val)} />
            </div>
          </div>
        );

      case 'results':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-1">Results Description</label>
              <RichTextEditor value={data.desc} onChange={val => handleChange('desc', val)} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {data.metrics.map((m, i) => (
                <div key={i} className="border p-3 rounded-lg bg-green-50 relative group">
                  <button onClick={() => removeListItem('metrics', i)} className="absolute top-1 right-1 text-red-400 opacity-0 group-hover:opacity-100 transition"><FaTimes/></button>
                  <input type="text" placeholder="Title" value={m.title} onChange={e => handleObjChange('metrics', i, 'title', e.target.value)} className="w-full mb-1 bg-transparent border-b border-green-200 focus:outline-none font-medium" />
                  <input type="text" placeholder="Value" value={m.value} onChange={e => handleObjChange('metrics', i, 'value', e.target.value)} className="w-full mb-1 bg-transparent border-b border-green-200 focus:outline-none text-xl font-bold text-green-700" />
                  <input type="text" placeholder="Suffix/Info" value={m.extra} onChange={e => handleObjChange('metrics', i, 'extra', e.target.value)} className="w-full bg-transparent focus:outline-none text-xs text-gray-500" />
                </div>
              ))}
              <button onClick={() => addObjItem('metrics', { title: '', value: '', extra: '' })} className="border-2 border-dashed border-green-300 rounded-lg flex items-center justify-center text-green-600 hover:bg-green-50 p-4 font-bold">+ Metric</button>
            </div>
          </div>
        );

      case 'technologies':
        return (
          <div className="space-y-4">
            <input type="text" placeholder="Section Title" value={data.title} onChange={e => handleChange('title', e.target.value)} className="w-full p-2 border rounded font-bold" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {data.items?.map((item, i) => (
                <div key={i} className="border p-3 rounded flex flex-col gap-2 relative group bg-white">
                  <button onClick={() => removeListItem('items', i)} className="absolute top-1 right-1 text-red-400 opacity-0 group-hover:opacity-100"><FaTimes/></button>
                  <input type="text" placeholder="Tech Name" value={item.name} onChange={e => handleObjChange('items', i, 'name', e.target.value)} className="p-1 border rounded" />
                  <select 
                    value={item.icon} 
                    onChange={e => handleObjChange('items', i, 'icon', e.target.value)} 
                    className="p-1 border rounded text-sm bg-gray-50"
                  >
                    {TECH_ICONS.map(icon => (
                      <option key={icon.value} value={icon.value}>{icon.label}</option>
                    ))}
                  </select>
                </div>
              ))}
              <button onClick={() => addObjItem('items', { name: '', icon: '' })} className="border-2 border-dashed border-gray-300 p-4 text-gray-500 rounded hover:bg-gray-50 flex items-center justify-center font-bold">+ Add Tech</button>
            </div>
          </div>
        );

      case 'competitiveComparison':
        return (
          <div className="space-y-4 overflow-x-auto">
            <div className="flex gap-4 mb-2">
               <input type="text" value={data.headers[0]} onChange={(e) => {const h = [...data.headers]; h[0]=e.target.value; setData({...data, headers:h})}} className="p-2 border rounded w-1/3 font-bold text-green-600" placeholder="Us Label" />
               <input type="text" value={data.headers[1]} onChange={(e) => {const h = [...data.headers]; h[1]=e.target.value; setData({...data, headers:h})}} className="p-2 border rounded w-1/3 font-bold text-gray-600" placeholder="Them Label" />
            </div>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th className="p-2 border-b">Feature</th>
                  <th className="p-2 border-b w-32 text-center">{data.headers[0]}</th>
                  <th className="p-2 border-b w-32 text-center">{data.headers[1]}</th>
                  <th className="p-2 border-b w-10"></th>
                </tr>
              </thead>
              <tbody>
                {data.rows?.map((row, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-2"><input type="text" value={row.feature} onChange={e => handleObjChange('rows', i, 'feature', e.target.value)} className="w-full p-1 border rounded" placeholder="Feature name" /></td>
                    <td className="p-2 text-center"><input type="checkbox" checked={row.us} onChange={e => handleObjChange('rows', i, 'us', e.target.checked)} className="w-5 h-5 text-green-600" /></td>
                    <td className="p-2 text-center"><input type="checkbox" checked={row.them} onChange={e => handleObjChange('rows', i, 'them', e.target.checked)} className="w-5 h-5 text-red-500" /></td>
                    <td className="p-2 text-center"><button onClick={() => removeListItem('rows', i)} className="text-red-400 hover:text-red-600"><FaTrash/></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button onClick={() => addObjItem('rows', { feature: '', us: true, them: false })} className="text-blue-600 font-bold text-sm">+ Add Comparison Row</button>
          </div>
        );

      case 'clients':
        return (
          <div className="space-y-4">
            {data.items?.map((item, i) => (
              <div key={i} className="border p-4 rounded-lg bg-white relative">
                <button onClick={() => removeListItem('items', i)} className="absolute top-2 right-2 text-red-400 hover:text-red-600"><FaTrash/></button>
                <div className="grid md:grid-cols-2 gap-4">
                  <input type="text" placeholder="Client Name" value={item.name} onChange={e => handleObjChange('items', i, 'name', e.target.value)} className="w-full p-2 border rounded" />
                  <input type="text" placeholder="Title/Position" value={item.title} onChange={e => handleObjChange('items', i, 'title', e.target.value)} className="w-full p-2 border rounded" />
                  <div className="col-span-2">
                    <textarea placeholder="Review text..." value={item.review} onChange={e => handleObjChange('items', i, 'review', e.target.value)} className="w-full p-2 border rounded" rows={2} />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-500">Stars:</label>
                    <input type="number" min="1" max="5" value={item.stars} onChange={e => handleObjChange('items', i, 'stars', parseInt(e.target.value))} className="w-16 p-2 border rounded" />
                  </div>
                  <div className="col-span-2">
                    <ImageUpload label="Client Avatar" value={item.image} onChange={val => handleObjChange('items', i, 'image', val)} />
                  </div>
                </div>
              </div>
            ))}
            <button onClick={() => addObjItem('items', { name: '', title: '', review: '', stars: 5, image: '' })} className="w-full py-2 border-2 border-dashed border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50">+ Add Client Review</button>
          </div>
        );

      default:
        return <div className="text-gray-400 italic p-4">No form available for {type}</div>;
    }
  };

  return (
    <div>
      {renderFields()}
      <div className="mt-4 flex justify-end">
        <button 
          onClick={() => onSave(data)}
          className="bg-green-600 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-green-700 shadow transition transform hover:scale-105"
        >
          <FaSave /> Save {type.replace('_', ' ')}
        </button>
      </div>
    </div>
  );
};

export default ProjectEditor;